import { useState, useEffect, useCallback } from 'react'
import { Users, Search, Loader2, ChevronDown, Building2, X } from 'lucide-react'
import { getValidAccessToken } from '../../lib/auth'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1'

const ROLES = ['hunter', 'creator', 'business', 'admin'] as const
type Role = (typeof ROLES)[number]

const ROLE_LABELS: Record<Role, string> = {
  hunter: 'Hunter',
  creator: 'Creator',
  business: 'Business',
  admin: 'Admin',
}

const ROLE_COLORS: Record<Role, string> = {
  hunter: 'bg-stone-100 text-stone-700',
  creator: 'bg-amber-100 text-amber-800',
  business: 'bg-blue-100 text-blue-800',
  admin: 'bg-red-100 text-red-700',
}

const ROLE_DOT_COLORS: Record<Role, string> = {
  hunter: 'bg-stone-400',
  creator: 'bg-amber-500',
  business: 'bg-blue-500',
  admin: 'bg-red-500',
}

interface User {
  id: string
  email: string
  display_name: string
  role: Role
  created_at: string
  hunts_completed: number
  business_name: string | null
  business_logo_url: string | null
  business_website: string | null
  business_description: string | null
}

interface BusinessFields {
  business_name: string
  business_logo_url: string
  business_website: string
  business_description: string
}

async function fetchUsers(search: string, page: number): Promise<{ items: User[]; total: number }> {
  const token = await getValidAccessToken()
  const params = new URLSearchParams({ page: String(page), per_page: '50' })
  if (search) params.set('search', search)
  const res = await fetch(`${API_URL}/admin/users?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!res.ok) throw new Error('Failed to load users')
  return res.json()
}

async function updateRole(userId: string, role: Role): Promise<void> {
  const token = await getValidAccessToken()
  const res = await fetch(`${API_URL}/admin/users/${userId}/role?role=${role}`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: 'Failed to update role' }))
    throw new Error(err.detail)
  }
}

async function updateBusinessProfile(userId: string, fields: BusinessFields): Promise<BusinessFields> {
  const token = await getValidAccessToken()
  const res = await fetch(`${API_URL}/admin/users/${userId}/business`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      business_name: fields.business_name,
      business_logo_url: fields.business_logo_url,
      business_website: fields.business_website,
      business_description: fields.business_description,
    }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: 'Failed to update business profile' }))
    throw new Error(err.detail)
  }
  return res.json()
}

function RoleDropdown({ user, onRoleChange }: { user: User; onRoleChange: (role: Role) => void }) {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleSelect = async (role: Role) => {
    setOpen(false)
    if (role === user.role) return
    try {
      setLoading(true)
      await updateRole(user.id, role)
      onRoleChange(role)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update role')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        disabled={loading}
        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-opacity hover:opacity-80 disabled:opacity-50 ${ROLE_COLORS[user.role]}`}
      >
        {loading ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <>
            {ROLE_LABELS[user.role]}
            <ChevronDown className="h-3 w-3" />
          </>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full z-20 mt-1 w-36 rounded-lg border border-stone-200 bg-white py-1 shadow-lg">
            {ROLES.map((role) => (
              <button
                key={role}
                onClick={() => handleSelect(role)}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-stone-50 ${
                  role === user.role ? 'font-medium text-[#78350f]' : 'text-stone-700'
                }`}
              >
                <span className={`inline-block h-2 w-2 rounded-full ${ROLE_DOT_COLORS[role]}`} />
                {ROLE_LABELS[role]}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function BusinessEditor({
  user,
  onClose,
  onSave,
}: {
  user: User
  onClose: () => void
  onSave: (fields: BusinessFields) => void
}) {
  const [fields, setFields] = useState<BusinessFields>({
    business_name: user.business_name ?? '',
    business_logo_url: user.business_logo_url ?? '',
    business_website: user.business_website ?? '',
    business_description: user.business_description ?? '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)
      const updated = await updateBusinessProfile(user.id, fields)
      onSave({
        business_name: updated.business_name ?? '',
        business_logo_url: updated.business_logo_url ?? '',
        business_website: updated.business_website ?? '',
        business_description: updated.business_description ?? '',
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-stone-200 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-stone-900">Business profile</h2>
            <p className="text-xs text-stone-500">{user.display_name} &middot; {user.email}</p>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-6 py-5">
          {user.role !== 'business' && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              This user's role is <strong>{ROLE_LABELS[user.role]}</strong>. To use this profile as
              a hunt sponsor, set their role to <strong>Business</strong>.
            </div>
          )}

          <div>
            <label className="mb-1 block text-xs font-medium text-stone-700">Business name</label>
            <input
              type="text"
              value={fields.business_name}
              onChange={(e) => setFields((f) => ({ ...f, business_name: e.target.value }))}
              className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:border-[#f59e0b] focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/20"
              placeholder="Acme Coffee Co."
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-stone-700">Logo URL</label>
            <input
              type="url"
              value={fields.business_logo_url}
              onChange={(e) => setFields((f) => ({ ...f, business_logo_url: e.target.value }))}
              className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:border-[#f59e0b] focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/20"
              placeholder="https://example.com/logo.png"
            />
            {fields.business_logo_url && (
              <img
                src={fields.business_logo_url}
                alt="Logo preview"
                className="mt-2 h-12 w-12 rounded-lg border border-stone-200 object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                }}
              />
            )}
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-stone-700">Website</label>
            <input
              type="url"
              value={fields.business_website}
              onChange={(e) => setFields((f) => ({ ...f, business_website: e.target.value }))}
              className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:border-[#f59e0b] focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/20"
              placeholder="https://example.com"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-stone-700">Description</label>
            <textarea
              rows={3}
              value={fields.business_description}
              onChange={(e) => setFields((f) => ({ ...f, business_description: e.target.value }))}
              className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:border-[#f59e0b] focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/20"
              placeholder="Local coffee roaster sponsoring downtown hunts."
            />
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-stone-200 px-6 py-4">
          <button
            onClick={onClose}
            className="rounded-lg border border-stone-200 px-4 py-2 text-sm text-stone-600 hover:bg-stone-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#78350f] to-[#92400e] px-4 py-2 text-sm font-medium text-white shadow-sm transition-opacity hover:opacity-90 disabled:opacity-60"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Save
          </button>
        </div>
      </div>
    </div>
  )
}

export default function UsersPage() {
  const [users, setUsers] = useState<User[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [editingUser, setEditingUser] = useState<User | null>(null)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await fetchUsers(search, 1)
      setUsers(data.items)
      setTotal(data.total)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users')
    } finally {
      setLoading(false)
    }
  }, [search])

  useEffect(() => { load() }, [load])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSearch(searchInput)
  }

  const handleRoleChange = (userId: string, role: Role) => {
    setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, role } : u)))
  }

  const handleBusinessSave = (userId: string, fields: BusinessFields) => {
    setUsers((prev) =>
      prev.map((u) =>
        u.id === userId
          ? {
              ...u,
              business_name: fields.business_name || null,
              business_logo_url: fields.business_logo_url || null,
              business_website: fields.business_website || null,
              business_description: fields.business_description || null,
            }
          : u
      )
    )
  }

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Users</h1>
          <p className="mt-1 text-sm text-stone-500">{total} total users</p>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by name or email"
              className="w-72 rounded-lg border border-stone-200 py-2 pl-9 pr-4 text-sm focus:border-[#f59e0b] focus:outline-none focus:ring-2 focus:ring-[#f59e0b]/20"
            />
          </div>
          <button
            type="submit"
            className="rounded-lg bg-gradient-to-r from-[#78350f] to-[#92400e] px-4 py-2 text-sm font-medium text-white shadow-sm transition-opacity hover:opacity-90"
          >
            Search
          </button>
          {search && (
            <button
              type="button"
              onClick={() => { setSearch(''); setSearchInput('') }}
              className="rounded-lg border border-stone-200 px-4 py-2 text-sm text-stone-600 hover:bg-stone-50"
            >
              Clear
            </button>
          )}
        </form>
      </div>

      {error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex h-48 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#f59e0b]" />
        </div>
      ) : users.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-stone-300 py-16">
          <Users className="mb-3 h-12 w-12 text-stone-300" />
          <p className="text-stone-500">{search ? 'No users match your search' : 'No users yet'}</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
          <table className="w-full">
            <thead>
              <tr className="border-b border-stone-200 bg-stone-50">
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-500">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-500">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-500">Business</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-500">Hunts</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-stone-500">Joined</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {users.map((u) => (
                <tr key={u.id} className="transition-colors hover:bg-stone-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 text-sm font-bold text-amber-800">
                        {u.display_name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="font-medium text-stone-900">{u.display_name}</p>
                        <p className="text-xs text-stone-500">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <RoleDropdown user={u} onRoleChange={(role) => handleRoleChange(u.id, role)} />
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => setEditingUser(u)}
                      className="inline-flex items-center gap-2 rounded-lg border border-stone-200 px-3 py-1.5 text-xs font-medium text-stone-700 transition-colors hover:bg-stone-50"
                    >
                      <Building2 className="h-3.5 w-3.5" />
                      {u.business_name || 'Set up'}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-sm text-stone-600">{u.hunts_completed}</td>
                  <td className="px-6 py-4 text-sm text-stone-500">{formatDate(u.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editingUser && (
        <BusinessEditor
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSave={(fields) => handleBusinessSave(editingUser.id, fields)}
        />
      )}
    </div>
  )
}
